import { AlertTriangle, CheckCircle2, FileText } from "lucide-react";
import type { Issue } from "../services/db/schema";
import { formatBytes } from "../services/app/validation";
import { PDF_LARGE_ISSUE_COUNT, PDF_LARGE_SIZE_WARNING_BYTES } from "../services/app/constants";
import {
  getExportReadiness
} from "../services/app/reportStatus";

interface ExportReadinessPanelProps {
  issues: Issue[];
  exportAnyway: boolean;
  online: boolean;
  onExportAnywayChange: (value: boolean) => void;
}

export function ExportReadinessPanel({
  issues,
  exportAnyway,
  online,
  onExportAnywayChange
}: ExportReadinessPanelProps): JSX.Element {
  const readiness = getExportReadiness(issues);
  const { incomplete, reasons } = readiness;
  const photoBytes = issues.reduce((sum, issue) => sum + issue.photoSizeBytes, 0);
  const large = issues.length > PDF_LARGE_ISSUE_COUNT || photoBytes > PDF_LARGE_SIZE_WARNING_BYTES;
  if (issues.length === 0) {
    return (
      <div className="notice" role="status">
        <FileText size={18} aria-hidden="true" /> Add at least one condition note before export.
      </div>
    );
  }
  if (incomplete.length === 0 && reasons.tooLong === 0 && !large) {
    return (
      <div className="notice" role="status">
        <CheckCircle2 size={18} aria-hidden="true" /> {issues.length} ready for PDF.
      </div>
    );
  }
  return (
    <div className="notice notice--warning">
      <AlertTriangle size={18} aria-hidden="true" /> Some condition notes need attention before export.
      {reasons.needsText > 0 && (
        <p>{formatReason(reasons.needsText, "Needs text")}</p>
      )}
      {reasons.processing > 0 && (
        <p>{formatReason(reasons.processing, "Voice note still processing")}</p>
      )}
      {reasons.needsReview > 0 && (
        <p>{formatReason(reasons.needsReview, "Review note before PDF")}</p>
      )}
      {reasons.spokenReady > 0 && (
        <p>{formatReason(reasons.spokenReady, "Spoken note ready for review")}</p>
      )}
      {reasons.tooLong > 0 && (
        <p>{formatReason(reasons.tooLong, "Note too long for PDF")}</p>
      )}
      {reasons.failed > 0 && (
        <p>{formatReason(reasons.failed, "Transcription failed")}</p>
      )}
      {large && (
        <p>
          Large report. This may take a moment. Estimated photo data: {formatBytes(photoBytes)}.
        </p>
      )}
      {readiness.blockingReasonCount > 0 && (
        <p>
          {online
            ? "Finish these items before generating the PDF so the report does not miss queued or unreviewed notes."
            : "Go online and let queued voice notes finish, then review every note before generating the PDF."}
        </p>
      )}
      {readiness.canContinueWithoutNote && (
        <>
          <p>Continuing without a note leaves incomplete condition notes out of the PDF text.</p>
          <label className="button">
            <input
              type="checkbox"
              checked={exportAnyway}
              onChange={(event) => onExportAnywayChange(event.target.checked)}
            />
            Continue without a note
          </label>
        </>
      )}
    </div>
  );
}

function formatReason(count: number, label: string): string {
  return `${label}: ${count} ${count === 1 ? "condition note" : "condition notes"}.`;
}
